import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

export function PermanentDeleteDialog({
  count,
  pending,
  onCancel,
  onConfirm,
}: {
  count: number;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef(document.activeElement as HTMLElement | null);

  useEffect(() => {
    cancelRef.current?.focus();
    return () => previousFocus.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4"
      onMouseDown={(event) => {
        if (!pending && event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="permanent-delete-title"
        aria-describedby="permanent-delete-description"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !pending) {
            event.preventDefault();
            onCancel();
            return;
          }
          if (event.key !== 'Tab') return;
          const buttons = [...(dialogRef.current?.querySelectorAll<HTMLButtonElement>(
            'button:not(:disabled)',
          ) ?? [])];
          if (buttons.length === 0) return;
          const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
          const next = event.shiftKey
            ? (current - 1 + buttons.length) % buttons.length
            : (current + 1) % buttons.length;
          event.preventDefault();
          buttons[next]?.focus();
        }}
        className="w-full max-w-md rounded-2xl border border-line bg-surface p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <span className="rounded-full bg-danger/10 p-2 text-danger">
            <AlertTriangle className="size-5" />
          </span>
          <div>
            <h2 id="permanent-delete-title" className="text-lg font-semibold">
              Delete forever?
            </h2>
            <p id="permanent-delete-description" className="mt-2 text-sm text-ink-muted">
              This permanently removes the Trash messages in{' '}
              {count === 1 ? 'this conversation' : `${count.toLocaleString()} conversations`}{' '}
              from the mail server. If deleted-mail recovery is enabled and Wintermute has a
              complete cached copy, you can restore it from Recently removed until it expires.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-full px-4 py-2 text-sm font-medium text-ink-muted hover:bg-hover disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="flex items-center gap-2 rounded-full bg-danger px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {pending ? <Loader2 className="size-4 animate-spin" /> : null}
            Delete forever
          </button>
        </div>
      </div>
    </div>
  );
}
